import { useEffect, useLayoutEffect, useRef } from 'react';

const MINIMUM_VISIBLE_RATIO = 0.3;

// Browser events need the committed callback before passive effects; SSR has no events to observe.
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

type ViewDurationEndedHandler = (view: { dwellTimeSeconds: number }) => void;

type UseLuminaViewEndedOptions = {
	onViewEnded?: ViewDurationEndedHandler;
	sessionId?: string;
};

export const useLuminaViewEnded = ({
	onViewEnded,
	sessionId,
}: UseLuminaViewEndedOptions): React.RefObject<HTMLDivElement> => {
	const containerRef = useRef<HTMLDivElement>(null);
	const onViewEndedRef = useRef(onViewEnded);

	useIsomorphicLayoutEffect(() => {
		onViewEndedRef.current = onViewEnded;
	}, [onViewEnded]);

	const trackingEnabled = Boolean(onViewEnded && sessionId);

	useEffect(() => {
		const container = containerRef.current;
		const ownerDocument = container?.ownerDocument;
		const ownerWindow = ownerDocument?.defaultView;
		if (!trackingEnabled || !container || !ownerDocument || !ownerWindow?.IntersectionObserver) {
			return;
		}

		let isVisibleEnough = false;
		let windowHasFocus = ownerDocument.hasFocus();
		let viewStartedAt: number | undefined;

		// Recover if document focus returned without a corresponding window focus event.
		// Focus is page-level; the card itself never needs to be clicked or focused.
		const canStartView = (): boolean =>
			isVisibleEnough &&
			ownerDocument.visibilityState === 'visible' &&
			(windowHasFocus || ownerDocument.hasFocus());

		const startView = (timestamp = ownerWindow.performance.now()): void => {
			if (viewStartedAt !== undefined || !canStartView()) {
				return;
			}

			viewStartedAt = timestamp;
		};

		const endView = (timestamp = ownerWindow.performance.now()): void => {
			if (viewStartedAt === undefined) {
				return;
			}

			const dwellTimeSeconds = Math.max(0, Math.round(timestamp - viewStartedAt) / 1000);
			viewStartedAt = undefined;

			onViewEndedRef.current?.({ dwellTimeSeconds });
		};

		const handleIntersection: IntersectionObserverCallback = (entries) => {
			const entry = entries.find(({ target }) => target === container);
			if (!entry) {
				return;
			}

			isVisibleEnough = entry.isIntersecting && entry.intersectionRatio >= MINIMUM_VISIBLE_RATIO;
			if (isVisibleEnough) {
				startView(entry.time);
			} else {
				endView(entry.time);
			}
		};

		const handleFocus = (): void => {
			windowHasFocus = true;
			startView();
		};
		const handleBlur = (): void => {
			windowHasFocus = false;
			endView();
		};
		const handleVisibilityChange = (): void => {
			if (ownerDocument.visibilityState === 'visible') {
				startView();
			} else {
				windowHasFocus = false;
				endView();
			}
		};
		const handlePageHide = (): void => {
			endView();
		};

		const observer = new ownerWindow.IntersectionObserver(handleIntersection, {
			threshold: MINIMUM_VISIBLE_RATIO,
		});
		observer.observe(container);
		ownerWindow.addEventListener('focus', handleFocus);
		ownerWindow.addEventListener('blur', handleBlur);
		ownerWindow.addEventListener('pagehide', handlePageHide);
		ownerDocument.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			observer.disconnect();
			ownerWindow.removeEventListener('focus', handleFocus);
			ownerWindow.removeEventListener('blur', handleBlur);
			ownerWindow.removeEventListener('pagehide', handlePageHide);
			ownerDocument.removeEventListener('visibilitychange', handleVisibilityChange);
			endView();
		};
	}, [sessionId, trackingEnabled]);

	return containerRef;
};
